import { useMemo } from 'react';
import { useTemplateContext } from '../context/TemplateContext';
import { useAssetContext } from '../context/AssetContext';
import { OUTPUT_PRESETS } from '../config';
import type { CompositionProps, OutputSettings } from '../types';
import type { TextFields } from '../components/AssetManager/TextInputs';

const DEFAULT_OUTPUT: OutputSettings = OUTPUT_PRESETS.HD_30;

/**
 * Assembles CompositionProps from the three context slices (template + assets + text).
 * Returns null when no template is selected yet.
 * Memoized — only recomputes when a context value changes.
 */
export function useCompositionProps(
  textFields: TextFields,
  outputSettings: OutputSettings = DEFAULT_OUTPUT
): CompositionProps | null {
  const { activeTemplate, state: templateState } = useTemplateContext();
  const { state: assetState } = useAssetContext();

  return useMemo(() => {
    if (!activeTemplate || !templateState.activeTemplateId) return null;

    return {
      templateId: templateState.activeTemplateId,
      templateConfig: activeTemplate,

      // Text fields
      headline: textFields.headline,
      subheadline: textFields.subheadline,
      breakingNewsText: textFields.breakingNewsText,
      tickerText: textFields.tickerText,
      reporterName: textFields.reporterName,
      location: textFields.location,
      date: textFields.date,

      // Assets (all optional — layers handle missing gracefully)
      assets: assetState.assets,

      outputSettings,
    };
  }, [activeTemplate, templateState.activeTemplateId, textFields, assetState.assets, outputSettings]);
}

/**
 * Returns { width, height, durationInFrames, fps } for the active template.
 * Used by the Player to know composition dimensions.
 */
export function useCompositionMeta(outputSettings: OutputSettings = DEFAULT_OUTPUT) {
  const { activeTemplate } = useTemplateContext();

  return useMemo(() => {
    if (!activeTemplate) return null;
    const [width, height] = activeTemplate.metadata.resolution.split('x').map(Number);
    const fps = outputSettings.fps;
    const durationInFrames = activeTemplate.metadata.duration * fps;
    return { width, height, fps, durationInFrames };
  }, [activeTemplate, outputSettings.fps]);
}
